import { ReactNode, useEffect, useRef } from 'react';
import { cn } from '../../lib/cn.js';
import { SkeletonRow } from '../ui/Skeleton.js';

export interface ColumnHeader {
  key: string;
  label: string;
  sortable?: boolean;
  className?: string;
  /** Explicit pixel width — only honoured when DataTable is rendered with `fixedLayout`. */
  width?: string;
}

interface DataTableProps<T> {
  columns: ColumnHeader[];
  hiddenColumns?: Set<string>;
  rows: T[];
  rowKey: (row: T) => string | number;
  renderRow: (row: T, visibleColumns: ColumnHeader[]) => ReactNode;
  isLoading?: boolean;
  skeletonCount?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  /** Whether rows have a checkbox column */
  selectable?: boolean;
  allSelected?: boolean;
  onSelectAll?: (selected: boolean) => void;
  emptyState?: ReactNode;
  className?: string;
  /**
   * Optional per-row className. Used by /landlords to apply the
   * `bg-primary/5` highlight to selected rows. Returned string is
   * appended to the outer `<tr>` className. Replaces the previous
   * pattern where row components returned their OWN <tr> — which
   * created nested-<tr> invalid HTML and broke `table-fixed` column
   * widths in the inner row (Sherzod 2026-05-03).
   */
  getRowClassName?: (row: T) => string;
  /**
   * Optional `data-*` attributes per row. The drawer's unit-number
   * deep link uses `data-unit-id` to scroll/highlight a specific
   * row (Sherzod 2026-05-06).
   */
  getRowDataAttrs?: (row: T) => Record<string, string | number | undefined>;
  /**
   * Use `table-layout: fixed` and render a `<colgroup>` derived from
   * `columns[].width`. Forces every body row to use the exact same column
   * widths as the header — fixes the alignment drift Sherzod called out
   * 2026-05-03.
   */
  fixedLayout?: boolean;
  /**
   * If `true` (default for back-compat with /buildings + /kanban which
   * use a row-actions menu), DataTable renders an extra empty `<th>` at
   * the end of the header row that body rows are expected to mirror with
   * a matching `<td>`. Set to `false` for tables that don't have that
   * trailing column (e.g. /landlords as of 2026-05-03).
   */
  trailingActionsColumn?: boolean;
}

export function DataTable<T>({
  columns,
  hiddenColumns = new Set(),
  rows,
  rowKey,
  renderRow,
  isLoading,
  skeletonCount = 8,
  sortBy,
  sortDir,
  onSort,
  selectable,
  allSelected,
  onSelectAll,
  emptyState,
  className,
  getRowClassName,
  getRowDataAttrs,
  fixedLayout = false,
  trailingActionsColumn = true,
}: DataTableProps<T>) {
  const visibleColumns = columns.filter((c) => !hiddenColumns.has(c.key));

  // Total cells per row: optional select + visible data + optional trailing actions
  const totalCols = visibleColumns.length + (selectable ? 1 : 0) + (trailingActionsColumn ? 1 : 0);

  // Reset horizontal scroll to leftmost on mount and whenever the row set
  // changes. Browsers sometimes restore scroll from session history; we
  // overwrite for this wrapper so users always see the leftmost column
  // first when entering /landlords. Sherzod 2026-05-03.
  const scrollWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollWrapRef.current) scrollWrapRef.current.scrollLeft = 0;
  }, [rows.length, visibleColumns.length]);

  return (
    <div ref={scrollWrapRef} className={cn('w-full overflow-x-auto', className)}>
      <table
        className={cn(
          'w-full border-collapse text-sm',
          fixedLayout && 'table-fixed'
        )}
      >
        {fixedLayout && (
          <colgroup>
            {selectable && <col style={{ width: '44px' }} />}
            {visibleColumns.map((col) => (
              <col key={col.key} style={col.width ? { width: col.width } : undefined} />
            ))}
            {trailingActionsColumn && <col style={{ width: '80px' }} />}
          </colgroup>
        )}
        <thead className="bg-[#f7f8fc] sticky top-0 z-10">
          <tr>
            {selectable && (
              <th className="px-4 py-3 w-10 border-b border-border">
                <input
                  type="checkbox"
                  checked={!!allSelected}
                  onChange={(e) => onSelectAll?.(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-border accent-primary"
                  aria-label="Select all rows"
                />
              </th>
            )}
            {visibleColumns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'text-left px-4 py-3 text-[10px] font-semibold text-secondary uppercase tracking-wide border-b border-border whitespace-nowrap',
                  col.sortable && onSort && 'cursor-pointer hover:text-primary-text select-none',
                  col.className
                )}
                style={fixedLayout && col.width ? { width: col.width } : undefined}
                onClick={() => col.sortable && onSort?.(col.key)}
                aria-sort={
                  sortBy === col.key
                    ? sortDir === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  {col.sortable && onSort && (
                    <span
                      className={cn(
                        'text-[8px] leading-none',
                        sortBy === col.key ? 'text-primary' : 'text-border'
                      )}
                      aria-hidden="true"
                    >
                      {sortBy === col.key && sortDir === 'desc' ? '▼' : '▲'}
                    </span>
                  )}
                </span>
              </th>
            ))}
            {/* Trailing actions column placeholder (for row-action "..." menus on /buildings, /kanban). */}
            {trailingActionsColumn && (
              <th className="px-4 py-3 w-20 border-b border-border" />
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {isLoading ? (
            Array.from({ length: skeletonCount }).map((_, i) => (
              <tr key={i}>
                <td colSpan={totalCols} className="px-0">
                  <SkeletonRow />
                </td>
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td
                colSpan={totalCols}
                className="py-16 text-center"
              >
                {emptyState ?? (
                  <p className="text-sm text-secondary">No results found</p>
                )}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={cn(
                  'hover:bg-hover-bg transition-colors group',
                  getRowClassName?.(row)
                )}
                {...(getRowDataAttrs?.(row) ?? {})}
              >
                {renderRow(row, visibleColumns)}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
